import { ChangeDetectorRef, Component } from '@angular/core';
import { Type } from 'class-transformer';
import { ClassTransformerHelper } from '../../../../helpers/class.transformer.helper';
import { WorkflowType } from '../../../../models/enums/workflowtype.enum';
import { DataModelService, DataSourceQuery } from '../../../../services/datamodel.service';
import { StoredProcedureService } from '../../../../services/stored.procedure.service';
import { WorkflowExitInfo, WorkflowModuleSettingsHelper, WorkflowRegistry } from '../../../../services/workflow.service';
import { DataCheck, FormulaWorkflowDialogContent } from '../../../workflow.dialog';

@Component({
    selector: 'wf-execute-stored-procedure-settings',
    templateUrl: './execute.stored.procedure.settings.html'
})
export class ExecuteStoredProcedureSettings extends FormulaWorkflowDialogContent {
    Data = new ExecuteStoredProcedureSettingsData();
    DataModels = [];
    DataSources = [];
    Procedures = [];
    Parameters = [];

    public static GetRegistry(): WorkflowRegistry {
        const reg = new WorkflowRegistry();
        reg.ID = 'executeStoredProcedureWFModule';
        reg.Caption = '@@StoredProcedure';
        reg.GroupID = 'containeroperations';
        reg.Index = 60;
        reg.SettingsControl = ExecuteStoredProcedureSettings;
        reg.SettingsTypeHelper = new ExecuteStoredProcedureSettingsDataHelper();
        reg.WorkflowType = WorkflowType.Service;
        return reg;
    }

    constructor(private dataService: DataModelService, private spService: StoredProcedureService, private cdRef: ChangeDetectorRef) {
        super();
    }

    ngOnInit() {
        this.dataService.GetModels().subscribe(models => {
            if (models) {
                this.DataModels = models;
                if (this.Data) {
                    if (this.DataModels.some(x => x.SID === this.Data.DataModel)) {
                        const query = new DataSourceQuery(this.Data.DataModel);
                        query.DBUsages = [[131072]]; // RelationalStoredProcedures
                        this.dataService.GetDataSourcesByQuery(query).subscribe(dataSources => {
                            if (dataSources) {
                                this.DataSources = dataSources;
                                if (dataSources.some(x => x.SID === this.Data.DataSource)) {
                                    this.spService.GetStoredProcedures(this.Data.DataSource).subscribe(procedures => {
                                        if (procedures) {
                                            this.Procedures = procedures;
                                            const p = procedures.find(x => x.Name == this.Data.Procedure);
                                            if (p) {
                                                if (p.Variables) {
                                                    p.Variables.forEach(v => {
                                                        if (!v.IsOutput) {
                                                            if (this.Data.Parameters) {
                                                                const pVal = this.Data.Parameters.find(x => x.Name == v.Name);
                                                                if (pVal) {
                                                                    v.Value = pVal.Value;
                                                                }
                                                            }
                                                            this.Parameters.push(v);
                                                        }
                                                    });
                                                }
                                            } else {
                                                this.Data.Procedure = null;
                                                this.Data.Parameters = [];
                                            }
                                        } else {
                                            this.Data.Procedure = null;
                                            this.Data.Parameters = [];
                                        }
                                        this.cdRef.detectChanges();
                                    });
                                } else {
                                    this.Data.DataSource = null;
                                    this.Data.Procedure = null;
                                    this.Data.Parameters = [];
                                }
                            } else {
                                this.Data.DataSource = null;
                                this.Data.Procedure = null;
                                this.Data.Parameters = [];
                            }
                            this.cdRef.detectChanges();
                        });
                    } else {
                        if (this.DataModels.length === 1) {
                            this.Data.DataModel = this.DataModels[0].SID;
                            this.modelChanged();
                        } else {
                            this.Data.DataModel = null;
                            this.Data.DataSource = null;
                            this.Data.Procedure = null;
                            this.Data.Parameters = [];
                        }
                    }
                }
            } else {
                if (this.Data) {
                    this.Data.DataModel = null;
                    this.Data.DataSource = null;
                    this.Data.Procedure = null;
                    this.Data.Parameters = [];
                }
            }
            this.cdRef.detectChanges();
        });
    }

    initialize(data: any) {
        super.initialize(data);
        if (data) {
            this.Data = ClassTransformerHelper.CopyObject(data, ExecuteStoredProcedureSettingsData);
        }
    }

    getResult(): any {
        const filled = [];
        if (this.Parameters) {
            this.Parameters.forEach(p => {
                if (p.Value) {
                    const pVal = new StoredProcedureParameter();
                    pVal.Name = p.Name;
                    pVal.Value = p.Value;
                    filled.push(pVal);
                }
            });
        }
        this.Data.Parameters = filled;
        return this.Data;
    }

    checkData(): DataCheck {
        const retVal = new DataCheck();
        if (!this.Data.Procedure) {
            retVal.IsCorrect = false;
            retVal.Error = '@@Bitte waehlen Sie eine Prozedur';
        }
        return retVal;
    }

    modelChanged() {
        this.DataSources = [];
        this.Procedures = [];
        this.Parameters = [];
        if (this.Data) {
            this.Data.DataSource = null;
            this.Data.Procedure = null;
            this.Data.Parameters = [];
            if (this.Data.DataModel) {
                const query = new DataSourceQuery(this.Data.DataModel);
                query.DBUsages = [[131072]]; // RelationalStoredProcedures
                this.dataService.GetDataSourcesByQuery(query).subscribe(x => {
                    if (x) {
                        this.DataSources = x;
                    }
                });
            }
        }
    }

    dataSourceChanged() {
        this.Procedures = [];
        this.Parameters = [];
        if (this.Data) {
            this.Data.Procedure = null;
            this.Data.Parameters = [];
            if (this.Data.DataSource) {
                this.spService.GetStoredProcedures(this.Data.DataSource).subscribe(procedures => {
                    if (procedures) {
                        this.Procedures = procedures;
                    }
                });
            }
        }
    }

    procedureChanged() {
        const parameters = [];
        if (this.Data) {
            this.Data.Parameters = [];
            if (this.Data.Procedure) {
                const prod = this.Procedures.find(x => x.Name == this.Data.Procedure);
                if (prod && prod.Variables) {
                    prod.Variables.forEach(x => {
                        x.Value = null;
                        if (!x.IsOutput) {
                            parameters.push(x);
                        }
                    });
                }
            }
        }
        this.Parameters = parameters;
    }
}

export class StoredProcedureParameter {
    Name: string;
    Value: string;
}

export class ExecuteStoredProcedureSettingsData {
    DataModel: string;
    DataSource: string;
    Procedure: string;
    @Type(() => StoredProcedureParameter)
    Parameters: StoredProcedureParameter[] = [];
}

export class ExecuteStoredProcedureSettingsDataHelper extends WorkflowModuleSettingsHelper {
    getExitPoints(settings: any): WorkflowExitInfo[] {
        return [new WorkflowExitInfo()];
    }
    getEmptySettingsInstance() {
        return new ExecuteStoredProcedureSettingsData();
    }
}
